/**
 * Iter 641 — Boot splash after a QR tap: branded full-screen cover while the
 * store loads. Store-front mark first; the store logo + name pop in the
 * moment /scan returns, hold ~250 ms, then the whole thing fades out.
 * (On web the static splash in public/index.html covers the JS bundle boot
 * before this component can even mount — same look, so it feels seamless.)
 * Never shown on the till, without a QR, or on ?resume= / ?paid= links.
 */
import React, { useEffect, useState } from "react";

const HOLD_MS = 250;
const FADE_MS = 250;

export default function BootSplash({ active, vendorName, vendorLogo, loaded, error }) {
  const [gone, setGone] = useState(!active);
  const [fading, setFading] = useState(false);

  // Store arrived (or errored): pop the logo, hold, fade, unmount.
  useEffect(() => {
    if (gone || fading) return undefined;
    if (!loaded && !error) return undefined;
    const t = setTimeout(() => setFading(true), error ? 0 : HOLD_MS);
    return () => clearTimeout(t);
  }, [loaded, error, gone, fading]);

  useEffect(() => {
    if (!fading) return undefined;
    const t = setTimeout(() => setGone(true), FADE_MS + 50);
    return () => clearTimeout(t);
  }, [fading]);

  // Never trap the customer: if the store takes >6 s, drop the splash anyway.
  useEffect(() => {
    if (gone) return undefined;
    const t = setTimeout(() => setFading(true), 6000);
    return () => clearTimeout(t);
  }, [gone]);

  if (gone) return null;

  return (
    <div
      data-testid="cust-boot-splash"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center"
      style={{
        backgroundColor: "#FAFAFC",
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        pointerEvents: fading ? "none" : "auto",
      }}
    >
      <div className="flex flex-col items-center px-8">
        <div
          className="flex flex-col items-center justify-center min-h-[160px]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: `scale(${loaded ? 1 : 0.8})`,
            transition: "opacity 300ms, transform 300ms",
          }}
        >
          {vendorLogo ? (
            <img
              src={vendorLogo}
              alt=""
              data-testid="cust-boot-logo"
              className="w-[132px] h-[132px] rounded-[28px] bg-white object-contain"
            />
          ) : (
            <div className="w-[132px] h-[132px] rounded-[28px] grid place-items-center" style={{ backgroundColor: "#EEF2FF" }}>
              <span className="text-[56px] leading-none">🏬</span>
            </div>
          )}
          <div
            data-testid="cust-boot-store"
            className="mt-3.5 text-center font-display text-xl font-black text-slate-900 line-clamp-2"
          >
            {vendorName || ""}
          </div>
        </div>
        <div className="mt-[18px] text-[13.5px] font-semibold text-slate-500">
          {loaded ? "Welcome — getting your visit ready…" : "Finding your store…"}
        </div>
      </div>
      <div className="absolute bottom-10 flex flex-col items-center">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">powered by</span>
        <span className="mt-1 font-display text-lg font-black text-slate-700">AllSale</span>
      </div>
    </div>
  );
}
